/**
 * Cleaning up the trash at once (admins), from the page's menu: what was deleted more than some days ago, or all of
 * it. One dialog walks through it: how old (for the first), what that is ("214 items · 3.2 GB", and the first of
 * them), the confirmation (their number typed), then the server's progress as it purges in steps, until it's done.
 */
import { useEffect, useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { CleanupPreview, Job, JobList, PageTrashRow, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { formatBytes, formatInstant } from "../../lib/format.ts";
import { spaceApi, spaceUrl } from "../../lib/paths.ts";
import { useAction, useResource } from "../../lib/resource.ts";
import { Button, Link } from "../../ui/Button.tsx";
import { Dialog, DialogActions } from "../../ui/Dialog.tsx";
import { Field, Form, Input, Select } from "../../ui/form.tsx";
import { ShredIcon } from "../../ui/icons.tsx";
import { Async } from "../../ui/misc.tsx";
import { LINK } from "../../ui/styles.ts";
import { KindIcon } from "../items/icons.tsx";
import { DeletedBy } from "./deletedBy.tsx";
import { typeCountLabel } from "./typeCount.ts";

/** Which cleanup: what's older than some days, or everything. */
export type CleanupMode = "older" | "all";

/** How often a running cleanup is asked how far it got. */
const POLL_MS = 1000;
/** How long in the trash, in days (the server's `days`): a week, a month, three, half a year, a year. */
const AGES = ["7", "30", "90", "182", "365"] as const;
const DEFAULT_DAYS = "30";
/** How many of what a cleanup takes are listed (the rest counted). */
const LISTED = 50;
const PERCENT = 100;
const DATE: Intl.DateTimeFormatOptions = { dateStyle: "medium" };

type Step = "days" | "preview" | "job";

/** The first of what a cleanup would delete (as the trash lists them), and how many more of its `count` there are. */
function Doomed({ space, before, count }: { space: SpaceOut; before: string; count: number }) {
  const rows = useResource(`trash-cleanup-rows:${space.id}:${before}`, (signal) =>
    api.get<PageTrashRow>(spaceApi(space, "/trash"), { deleted_before: before, limit: LISTED }, signal),
  );
  return (
    <Async r={rows}>
      {(page) => {
        const more = count - page.data.length;
        return (
          <div class="max-h-64 overflow-y-auto rounded-md border border-zinc-200">
            <ul aria-label={t("trash.cleanup.list")} class="divide-y divide-zinc-100 text-sm">
              {page.data.map((row) => (
                <li key={row.id} class="flex items-start gap-3 px-3 py-2">
                  <KindIcon item={row} />
                  <span class="min-w-0 flex-1">
                    <span class="block text-zinc-900 [overflow-wrap:anywhere]">
                      {row.title || t("common.untitled")}
                    </span>
                    <span class="text-xs">
                      <DeletedBy row={row} />
                    </span>
                  </span>
                  <span class="shrink-0 text-zinc-600 tabular-nums">{row.file ? formatBytes(row.file.size) : "—"}</span>
                </li>
              ))}
            </ul>
            {more > 0 ? (
              <p class="border-t border-zinc-100 px-3 py-2 text-sm text-zinc-500">
                {t("trash.cleanup.more", { count: more })}
              </p>
            ) : null}
          </div>
        );
      }}
    </Async>
  );
}

/** Where a cleanup got to: a bar while it runs, then what it freed (or how far it got before it stopped). */
function Progress({ space, job }: { space: SpaceOut; job: Job }) {
  if (job.running) {
    const share = job.total > 0 ? Math.round((job.done / job.total) * PERCENT) : 0;
    return (
      <div class="flex flex-col gap-2">
        <p role="status" class="text-sm text-zinc-700 tabular-nums">
          {t("trash.cleanup.running", { done: job.done, total: job.total })}
        </p>
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={PERCENT}
          aria-valuenow={share}
          class="h-2 w-full overflow-hidden rounded-full bg-zinc-100"
        >
          <div class="h-full rounded-full bg-blue-600 transition-[width]" style={{ width: `${share}%` }} />
        </div>
      </div>
    );
  }
  return (
    <p role="status" class={`text-sm ${job.error ? "text-red-700" : "text-zinc-700"}`}>
      <span>
        {job.error
          ? t("trash.cleanup.failed", { done: job.done, total: job.total })
          : t("trash.cleanup.done", { count: job.done, size: formatBytes(job.bytes) })}
      </span>{" "}
      {job.commit ? (
        <Link href={spaceUrl(space, `/history/${job.commit}`)} class={LINK}>
          {t("trash.cleanup.history")}
        </Link>
      ) : null}
    </p>
  );
}

/** What it would delete, and the go-ahead. */
function Preview(p: {
  space: SpaceOut;
  mode: CleanupMode;
  days: string;
  onBack: () => void;
  onClose: () => void;
  onStarted: (job: Job) => void;
}) {
  const preview = useResource(`trash-cleanup-preview:${p.space.id}:${p.days}`, (signal) =>
    api.get<CleanupPreview>(spaceApi(p.space, "/trash/cleanup"), { days: Number(p.days) }, signal),
  );
  const [start, starting] = useAction(async () => {
    p.onStarted(await api.post<Job>(spaceApi(p.space, "/trash/cleanup"), { days: Number(p.days) }));
  });
  const [typed, setTyped] = useState("");
  const back = p.mode === "older" ? <Button onClick={p.onBack}>{t("common.back")}</Button> : null;
  return (
    <Async r={preview}>
      {(x) => {
        const date = formatInstant(x.before, DATE);
        if (x.items === 0) {
          return (
            <>
              <p class="text-sm text-zinc-700">
                {p.mode === "all" ? t("trash.empty") : t("trash.cleanup.none", { date })}
              </p>
              <DialogActions>
                {back}
                <Button onClick={p.onClose}>{t("common.done")}</Button>
              </DialogActions>
            </>
          );
        }
        const count = String(x.items);
        return (
          <>
            <p class="text-lg font-semibold text-zinc-900 tabular-nums">
              {t("trash.cleanup.preview", { count: x.items, size: formatBytes(x.bytes) })}
            </p>
            <Doomed space={p.space} before={x.before} count={x.items} />
            <p class="text-sm text-zinc-700">
              {p.mode === "all"
                ? t("trash.cleanup.confirmAll", { count: x.items })
                : t("trash.cleanup.confirmOlder", { count: x.items, date })}
            </p>
            <Field label={typeCountLabel(x.items)}>
              <Input value={typed} onValue={setTyped} autocomplete="off" inputMode="numeric" />
            </Field>
            <DialogActions>
              {back ?? (
                <Button answer="cancel" onClick={p.onClose}>
                  {t("common.cancel")}
                </Button>
              )}
              <Button
                answer="confirm"
                variant="danger"
                busy={starting}
                disabled={typed.trim() !== count}
                onClick={() => start()}
              >
                <ShredIcon />
                {t("trash.purge")}
              </Button>
            </DialogActions>
          </>
        );
      }}
    </Async>
  );
}

/** The dialog's steps, from the start each time it opens (or at the progress, when one is running). */
function Steps(p: { space: SpaceOut; mode: CleanupMode; onClose: () => void; onDone: () => void }) {
  const [step, setStep] = useState<Step>(p.mode === "all" ? "preview" : "days");
  const [days, setDays] = useState(p.mode === "all" ? "0" : DEFAULT_DAYS);
  const [followed, setFollowed] = useState(false);
  // The space's cleanup running, or the last one (the newest first).
  const job = useResource(`trash-cleanup:${p.space.id}`, (signal) =>
    api.get<JobList>(spaceApi(p.space, "/jobs"), { action: "cleanup" }, signal),
  );
  const current = job.data?.data[0];
  const running = current?.running === true;
  // While it runs, ask again a moment after each answer; once it stopped, the trash changed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: each answer is what's waited for.
  useEffect(() => {
    const timer = running ? setTimeout(job.reload, POLL_MS) : undefined;
    if (running) {
      setFollowed(true);
    } else if (followed) {
      setFollowed(false);
      p.onDone();
    }
    return () => clearTimeout(timer);
  }, [job.data]);
  if ((step === "job" || running) && current) {
    return (
      <>
        <Progress space={p.space} job={current} />
        <DialogActions>
          <Button answer="confirm" variant={running ? "secondary" : "primary"} onClick={p.onClose}>
            {running ? t("common.dismiss") : t("common.done")}
          </Button>
        </DialogActions>
      </>
    );
  }
  if (step !== "days") {
    return (
      <Preview
        space={p.space}
        mode={p.mode}
        days={days}
        onBack={() => setStep("days")}
        onClose={p.onClose}
        onStarted={(started) => {
          setStep("job");
          job.set({ data: [started] });
        }}
      />
    );
  }
  return (
    <Form class="flex flex-col gap-4" onSubmit={() => setStep("preview")}>
      <Field label={t("trash.cleanup.days")} hint={t("trash.cleanup.hint")}>
        <Select
          value={days}
          onValue={setDays}
          options={AGES.map((d) => ({ value: d, label: t(`trash.cleanup.age.${d}`) }))}
        />
      </Field>
      <DialogActions>
        <Button answer="cancel" onClick={p.onClose}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" submit={true}>
          {t("common.continue")}
        </Button>
      </DialogActions>
    </Form>
  );
}

export function CleanupDialog(p: {
  space: SpaceOut;
  mode: CleanupMode | null;
  onClose: () => void;
  onDone: () => void;
}) {
  return (
    <Dialog
      open={p.mode !== null}
      onClose={p.onClose}
      title={p.mode === "all" ? t("trash.emptyTrash") : t("trash.cleanup.titleOlder")}
    >
      {p.mode ? <Steps space={p.space} mode={p.mode} onClose={p.onClose} onDone={p.onDone} /> : null}
    </Dialog>
  );
}
